/**
 * F38 (Wave 2 Track D) — admin "create a one-off subscription invoice" modal.
 *
 * Composes Modal + FormField + CurrencyInput (USD minor units) + Textarea
 * (mandatory reason ≥10). Emits a manual `commission_statements` row with
 * source `venue_subscription` (server-side). Gated on
 * `venue_subscription:override`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, CurrencyInput, FormField, Modal, Textarea } from '@bravemove/ui-web';
import type { ManualSubscriptionInvoiceInput } from '@bravemove/types';

export interface ManualInvoiceDialogProps {
  readonly open: boolean;
  readonly venueLabel: string;
  readonly onConfirm: (input: ManualSubscriptionInvoiceInput) => Promise<void>;
  readonly onClose: () => void;
}

export function ManualInvoiceDialog({
  open,
  venueLabel,
  onConfirm,
  onClose,
}: ManualInvoiceDialogProps) {
  const { t } = useI18n();
  const [amount, setAmount] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    /* eslint-disable react-hooks/set-state-in-effect */
    setAmount(0);
    setReason('');
    setServerError(null);
    setSubmitting(false);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [open]);

  const reasonTrimmed = reason.trim();
  const reasonValid = reasonTrimmed.length >= 10 && reasonTrimmed.length <= 500;
  const amountValid = Number.isInteger(amount) && amount >= 0;
  const disableSubmit = submitting || !reasonValid || !amountValid;

  const submit = async () => {
    setServerError(null);
    if (!reasonValid || !amountValid) {
      setServerError(
        t('admin:subscription.dialog.manual_invoice.error_invalid', {
          defaultValue: 'Provide a non-negative amount and a reason (10–500 characters).',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({ amount, reason: reasonTrimmed });
      onClose();
    } catch {
      setServerError(
        t('admin:subscription.toast.error_generic', {
          defaultValue: 'Something went wrong. Please try again.',
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:subscription.dialog.manual_invoice.title', {
        defaultValue: 'Create a one-off invoice',
      })}
      description={t('admin:subscription.dialog.manual_invoice.description', {
        defaultValue: 'Issues a manual subscription invoice for this venue. Audit-logged.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="manual-invoice-cancel"
          >
            {t('common:cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="manual-invoice-confirm"
          >
            {t('admin:subscription.dialog.manual_invoice.submit', {
              defaultValue: 'Create invoice',
            })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="manual-invoice-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <p className="text-[12px] text-ink-3">
          {t('admin:subscription.detail.venue', { defaultValue: 'Venue' })}:{' '}
          <span className="font-semibold text-ink">{venueLabel}</span>
        </p>

        <FormField
          label={t('admin:subscription.dialog.manual_invoice.amount_label', {
            defaultValue: 'Amount',
          })}
        >
          <CurrencyInput
            value={amount}
            currency="USD"
            ariaLabel={t('admin:subscription.dialog.manual_invoice.amount_label', {
              defaultValue: 'Amount',
            })}
            onChange={(v) => setAmount(v)}
          />
        </FormField>

        <FormField
          label={t('admin:subscription.dialog.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          required
        >
          <Textarea
            rows={3}
            maxLength={500}
            value={reason}
            data-testid="manual-invoice-reason"
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>

        {serverError ? (
          <div data-testid="manual-invoice-server-error">
            <Banner tone="danger">{serverError}</Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default ManualInvoiceDialog;
